// src/components/rpa/agent/AnomalyEscalationCard.js
// Shows anomalies extracted after each audit step.
// Each anomaly can be added as an issue to the existing AuditIssue system
// via POST /admin/rpa/docs/${docId}/issues

import React, { useState } from 'react';
import axios from 'axios';
import './AnomalyEscalationCard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const SEVERITY_COLORS = {
  critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', badge: '#dc2626' },
  high:     { bg: '#fff7ed', border: '#fdba74', text: '#92400e', badge: '#f59e0b' },
  moderate: { bg: '#eff6ff', border: '#93c5fd', text: '#1e3a8a', badge: '#2563eb' },
  low:      { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', badge: '#6b7280' },
};

const TYPE_LABELS = {
  material_weakness:      'Material Weakness',
  significant_deficiency: 'Significant Deficiency',
  finding:                'Finding',
  observation:            'Observation',
  control_deficiency:     'Control Deficiency',
  compliance_violation:   'Compliance Violation',
  other:                  'Other',
};

const AnomalyEscalationCard = ({ docId, stepId, stepLabel, anomalies: initialAnomalies }) => {
  const [anomalies,  setAnomalies]  = useState(initialAnomalies.map((a, i) => ({ ...a, _id: i, selected: true, editing: false, saving: false, saved: false, dismissed: false })));
  const [bulkSaving, setBulkSaving] = useState(false);
  const [expanded,   setExpanded]   = useState(true);

  const visible = anomalies.filter(a => !a.dismissed);
  const selected = visible.filter(a => a.selected && !a.saved);

  const update = (id, patch) => setAnomalies(prev => prev.map(a => a._id === id ? { ...a, ...patch } : a));

  const addIssue = async (a) => {
    update(a._id, { saving: true });
    try {
      await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/issues`,
        {
          title:       a.title,
          description: a.description,
          type:        a.type,
          severity:    a.severity,
          amount:      a.amount || null,
          workpaperRef:a.workpaperRef || null,
          tags:        ['agent-flagged', stepId],
        },
        { withCredentials: true }
      );
      update(a._id, { saving: false, saved: true, selected: false });
    } catch (err) {
      update(a._id, { saving: false });
      alert(err.response?.data?.error || 'Failed to create issue');
    }
  };

  const addSelected = async () => {
    setBulkSaving(true);
    for (const a of selected) await addIssue(a);
    setBulkSaving(false);
  };

  if (!visible.length) return null;
  const savedCount = anomalies.filter(a => a.saved).length;

  return (
    <div className="ac-card">
      <div className="ac-header" onClick={() => setExpanded(!expanded)}>
        <div className="ac-header-left">
          <span className="ac-icon">⚠️</span>
          <div>
            <div className="ac-title">
              {visible.length} Anomaly{visible.length !== 1 ? 'ies' : ''} Flagged
              {savedCount > 0 && <span className="ac-saved-badge">{savedCount} added as issues</span>}
            </div>
            <div className="ac-sub">After {stepLabel} — click to review and add as issues</div>
          </div>
        </div>
        <span className="ac-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="ac-body">
          {visible.map(a => {
            const colors = SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.moderate;
            return (
              <div key={a._id} className={`ac-item ${a.saved ? 'saved' : ''}`} style={{ borderColor: colors.border, background: a.saved ? '#f0fdf4' : colors.bg }}>
                <div className="ac-item-top">
                  {!a.saved && (
                    <input type="checkbox" checked={a.selected} onChange={e => update(a._id, { selected: e.target.checked })} className="ac-check" />
                  )}
                  <div className="ac-item-content">
                    <div className="ac-item-header">
                      <span className="ac-severity-badge" style={{ background: colors.badge }}>{a.severity}</span>
                      <span className="ac-type-badge">{TYPE_LABELS[a.type] || a.type}</span>
                      {a.workpaperRef && <span className="ac-wp-badge">{a.workpaperRef}</span>}
                      {a.amount > 0 && <span className="ac-amount">${a.amount.toLocaleString()}</span>}
                      {a.saved && <span className="ac-saved-tag">✅ Added as Issue</span>}
                    </div>

                    {a.editing ? (
                      <input
                        className="ac-title-input"
                        value={a.title}
                        onChange={e => update(a._id, { title: e.target.value })}
                        onBlur={() => update(a._id, { editing: false })}
                        autoFocus
                      />
                    ) : (
                      <div className="ac-item-title" style={{ color: colors.text }} onClick={() => !a.saved && update(a._id, { editing: true })}>
                        {a.title}
                        {!a.saved && <span className="ac-edit-hint">✏️</span>}
                      </div>
                    )}

                    <div className="ac-item-desc">{a.description}</div>
                  </div>
                  {!a.saved && (
                    <div className="ac-item-actions">
                      <button className="ac-add-btn" onClick={() => addIssue(a)} disabled={a.saving}>
                        {a.saving ? '…' : '+ Issue'}
                      </button>
                      <button className="ac-dismiss-btn" onClick={() => update(a._id, { dismissed: true })}>✕</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {selected.length > 1 && (
            <div className="ac-bulk-row">
              <button className="ac-bulk-btn" onClick={addSelected} disabled={bulkSaving}>
                {bulkSaving ? 'Adding…' : `+ Add ${selected.length} Selected as Issues`}
              </button>
              <button className="ac-bulk-dismiss" onClick={() => setAnomalies(prev => prev.map(a => ({ ...a, dismissed: true })))}>
                Dismiss All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnomalyEscalationCard;